import { useState } from 'react';
import { Search, Grid2X2, List, Package2, SearchX, ChevronRight, X } from 'lucide-react';
import { ItemDetailSheet } from './item-detail-sheet.jsx';
import { NeuesItemSheet } from './neues-item-sheet.jsx';
import { EmptyState, SkeletonTroedelItem } from '../../components/boothly/index.js';
import { Portal } from '../../components/boothly/portal.jsx';
import { ActionButton } from '../../action-button.jsx';
import { useAnySheetOpen } from '../../widgets.jsx';
import { fmtCurrency } from '../../lib/format.js';

// Nur Abweichungen vom Normalzustand bekommen ein Label (Farbe = Bedeutung)
const statusPill = {
  reserviert: { label: 'Reserviert', cls: 'bg-[var(--tint-yellow)] text-[var(--label-primary)]' },
  verkauft:   { label: 'Verkauft',   cls: 'bg-[var(--tint-stand)] text-[var(--accent-stand)]' },
};

export function Inventar({
  items,
  categories,
  onCreateItem,
  onUpdateItem,
  onDeleteItem,
  onUploadItemPhoto,
  getPhotoUrl,
  dark,
  store,
  authToken,
  loading,
  runningMarket,
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('Alle');
  const [viewMode, setViewMode] = useState('list');
  const [selectedItem, setSelectedItem] = useState(null);
  const [newItemOpen, setNewItemOpen] = useState(false);
  const anySheetOpen = useAnySheetOpen();

  const allCategories = ['Alle', ...(categories ?? []).map(c => c?.name ?? c)];

  const filteredItems = (items ?? []).filter(item => {
    // Verkaufte Items komplett aus dem Sortiment ausblenden (persistenter Status bzw.
    // optimistisch). History bleibt in der Markttag-/Session-Übersicht.
    if (item.status === 'verkauft' || item.sold) return false;
    const itemCat = item.metadata?.category ?? item.category;
    if (activeCategory !== 'Alle' && itemCat !== activeCategory) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return item.name.toLowerCase().includes(q);
    }
    return true;
  });

  const getImageSrc = (item) => {
    const url = (getPhotoUrl && item.image_key) ? getPhotoUrl(item.image_key) : (item.image || null);
    if (!url) return null;
    return item._photoV ? `${url}?v=${item._photoV}` : url; // Cache-Bust nach Re-Upload
  };

  const renderThumb = (item, size) => {
    const imgSrc = getImageSrc(item);
    return imgSrc ? (
      <img src={imgSrc} alt="" loading="lazy" decoding="async" className="h-full w-full object-cover" />
    ) : (
      <div className="flex h-full w-full items-center justify-center bg-[var(--tint-troedel)]">
        <Package2 className={size === 'lg' ? 'h-8 w-8 text-[var(--accent-troedel)]' : 'h-5 w-5 text-[var(--accent-troedel)]'} />
      </div>
    );
  };
  const catOf = (item) => item.metadata?.category ?? item.category;

  return (
    <div data-testid="screen-troedel-sortiment" className="space-y-3 pt-2" style={{ paddingBottom: 'calc(150px + env(safe-area-inset-bottom, 0px))' }}>
      {/* Suche + Ansicht */}
      <div className="flex items-center gap-2">
        <label className="flex h-11 min-w-0 flex-1 items-center gap-2 rounded-[12px] bg-[var(--fill-tertiary)] px-3">
          <Search className="h-[18px] w-[18px] shrink-0 text-[var(--label-secondary)]" />
          <input
            type="search"
            enterKeyHint="search"
            placeholder="Im Sortiment suchen"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="min-w-0 flex-1 bg-transparent text-[17px] outline-none placeholder:text-[var(--label-secondary)] [&::-webkit-search-cancel-button]:hidden"
          />
          {searchQuery && (
            <button type="button" onClick={() => setSearchQuery('')} aria-label="Suche löschen" className="-mr-2 flex h-11 w-9 items-center justify-center">
              <span className="flex h-[18px] w-[18px] items-center justify-center rounded-full bg-[var(--label-tertiary)]">
                <X className="h-3 w-3 text-[var(--bg-L1)]" strokeWidth={3} />
              </span>
            </button>
          )}
        </label>
        <button
          type="button"
          data-testid="inventar-view-toggle"
          onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
          aria-label={viewMode === 'grid' ? 'Als Liste anzeigen' : 'Als Raster anzeigen'}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[12px] bg-[var(--fill-tertiary)] text-[var(--accent-troedel)] active:bg-[var(--fill-primary)]"
        >
          {viewMode === 'grid' ? <List className="h-5 w-5" /> : <Grid2X2 className="h-5 w-5" />}
        </button>
      </div>

      {/* Kategorien */}
      {allCategories.length > 1 && (
        <div className="scrollable-x -mx-5 flex gap-2 overflow-x-auto px-5">
          {allCategories.map(cat => {
            const active = activeCategory === cat;
            return (
              <button
                key={cat}
                data-testid={cat === 'Alle' ? 'filter-pill-alle' : `filter-pill-kategorie-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={() => setActiveCategory(cat)}
                aria-pressed={active}
                className="flex h-11 shrink-0 items-center"
              >
                <span
                  className={`whitespace-nowrap rounded-full px-3.5 py-[7px] text-[15px] font-semibold ${
                    active ? 'bg-[var(--tint-troedel)] text-[var(--accent-troedel)]' : 'bg-[var(--fill-tertiary)] text-[var(--label-primary)]'
                  }`}
                  style={{ transition: 'background-color 150ms var(--ease-out-ios), color 150ms var(--ease-out-ios)' }}
                >
                  {cat}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Artikel */}
      {loading ? (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <SkeletonTroedelItem key={i} />
          ))}
        </div>
      ) : (items ?? []).length === 0 ? (
        <EmptyState
          icon={Package2}
          title="Dein Sortiment ist leer"
          description="Füge Artikel hinzu, die du auf Märkten oder Flohmärkten verkaufst."
          cta={{
            label: 'Ersten Artikel hinzufügen',
            accent: 'troedel',
            onClick: () => setNewItemOpen(true),
          }}
        />
      ) : filteredItems.length === 0 ? (
        <EmptyState
          variant="compact"
          icon={SearchX}
          title={searchQuery ? 'Keine Treffer' : 'Keine Artikel in dieser Kategorie'}
          cta={searchQuery
            ? { label: 'Suche zurücksetzen', accent: 'troedel', onClick: () => setSearchQuery('') }
            : { label: 'Alle Kategorien anzeigen', accent: 'troedel', onClick: () => setActiveCategory('Alle') }}
        />
      ) : viewMode === 'grid' ? (
        <>
          <p className="px-4 pt-1 text-[13px] text-[var(--label-secondary)]">{`${filteredItems.length} Artikel`}</p>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-4">
            {filteredItems.map(item => {
              const pill = statusPill[item.status];
              return (
                <button
                  key={item.id}
                  data-testid="item-card"
                  data-item-id={item.id}
                  onClick={() => setSelectedItem(item)}
                  className="overflow-hidden rounded-[16px] bg-[var(--bg-L1)] text-left active:scale-[0.98]"
                  style={{ transition: 'transform 100ms var(--ease-out-ios)' }}
                >
                  <div className="relative aspect-square">
                    {renderThumb(item, 'lg')}
                    {pill && (
                      <span className={`absolute left-2 top-2 rounded-[6px] px-1.5 py-0.5 text-[12px] font-semibold ${pill.cls}`}>{pill.label}</span>
                    )}
                  </div>
                  <div className="px-3 pb-3 pt-2">
                    <p className="line-clamp-2 min-h-[2.5em] text-[15px] leading-[1.25]">{item.name}</p>
                    <p className="num mt-1 text-[17px] font-semibold">{item.price != null ? fmtCurrency(item.price) : ''}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      ) : (
        <div>
          <p className="px-4 pb-1.5 pt-1 text-[13px] text-[var(--label-secondary)]">{`${filteredItems.length} Artikel`}</p>
          <div className="overflow-hidden rounded-[16px] bg-[var(--bg-L1)]">
            {filteredItems.map((item, idx) => {
              const pill = statusPill[item.status];
              const cat = catOf(item);
              return (
                <button
                  key={item.id}
                  data-testid="item-card"
                  data-item-id={item.id}
                  onClick={() => setSelectedItem(item)}
                  className="flex min-h-[68px] w-full items-center gap-3 pl-3 pr-3 text-left active:bg-[var(--fill-quaternary)]"
                  style={{ transition: 'background-color 120ms var(--ease-out-ios)' }}
                >
                  <div className="h-12 w-12 shrink-0 overflow-hidden rounded-[10px]">
                    {renderThumb(item)}
                  </div>
                  <div className={`flex min-h-[68px] min-w-0 flex-1 items-center gap-2 py-2.5 ${idx ? 'border-t border-[var(--separator)]' : ''}`}>
                    <div className="min-w-0 flex-1">
                      <p className="line-clamp-2 text-[17px] leading-[1.25]">{item.name}</p>
                      {(cat || pill) && (
                        <div className="mt-0.5 flex items-center gap-1.5">
                          {cat && <span className="truncate text-[13px] text-[var(--label-secondary)]">{cat}</span>}
                          {pill && <span className={`shrink-0 rounded-[6px] px-1.5 py-px text-[12px] font-semibold ${pill.cls}`}>{pill.label}</span>}
                        </div>
                      )}
                    </div>
                    <span className="num shrink-0 text-[17px] font-semibold">{item.price != null ? fmtCurrency(item.price) : ''}</span>
                    <ChevronRight className="h-4 w-4 shrink-0 text-[var(--label-tertiary)]" strokeWidth={2.6} />
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Action-Button — via Portal so position:fixed is relative to viewport, not SwipeableTabs transform */}
      {!anySheetOpen && !runningMarket && (
        <Portal>
          <ActionButton
            variant="floating"
            accent="var(--accent-troedel)"
            label="Neues Item"
            onClick={() => setNewItemOpen(true)}
          />
        </Portal>
      )}

      {/* Sheets */}
      {/* Portal: position:fixed relativ zum Viewport (Backdrop deckt auch den Header ab) */}
      <Portal>
      <ItemDetailSheet
        item={selectedItem}
        open={!!selectedItem}
        onOpenChange={(o) => { if (!o) setSelectedItem(null); }}
        onUpdateItem={onUpdateItem}
        onDeleteItem={onDeleteItem}
        onUploadItemPhoto={onUploadItemPhoto}
        getPhotoUrl={getPhotoUrl}
        dark={dark}
        categories={(categories ?? []).map(c => c?.name ?? c)}
      />
      </Portal>
      <NeuesItemSheet
        open={newItemOpen}
        onOpenChange={setNewItemOpen}
        onCreateItem={onCreateItem}
        categories={(categories ?? []).map(c => c?.name ?? c)}
        dark={dark}
        store={store}
        authToken={authToken}
      />
    </div>
  );
}
